import { contato, nomeParceiro, nomePessoa, telefoneFormatado } from '../../nucleo/formato';

export type Canal = 'whatsapp' | 'instagram' | 'email' | 'portal' | 'site';
export type EstadoConversa = 'aberta' | 'pendente' | 'encerrada';
export type StatusMensagem = 'enviando' | 'enviado' | 'entregue' | 'lido' | 'falhou';

export interface Conversa {
  id: string;
  canal: Canal;
  /** Nulo na conversa de e-mail com remetente sem cadastro: a tela mostra o e-mail do contato. */
  parceiro_id: string | null;
  /** Nulo quando o contato é de uma empresa que a pessoa não vê (W20): a tela usa `nomeParceiro`. */
  parceiro_nome: string | null;
  /** Nome declarado pelo remetente de e-mail sem contato; só para censurar no modo discreto. */
  remetente_nome?: string | null;
  /** Dona da caixa pessoal da conversa de e-mail sem contato; só ela cadastra o contato. */
  caixa_pessoal_dono_id?: string | null;
  parceiro_telefone: string | null;
  contato_externo: string;
  atendente_id: string | null;
  atendente_nome: string | null;
  estado: EstadoConversa;
  nao_lidas: number;
  ultima_mensagem_em: string | null;
  ultima_mensagem_resumo: string | null;
  created_at: string;
  /** Fase 2 (W9): fila, e-mail e a janela de 24 h do WhatsApp (null fora do WhatsApp). */
  fila_id?: string | null;
  fila_nome?: string | null;
  caixa?: string | null;
  assunto?: string | null;
  ultima_entrada_em?: string | null;
  janela_expira_em?: string | null;
  /** Trilha 5b (Digisac): etiquetas, prioridade e o menu de entrada esperando a escolha do contato. */
  etiquetas?: string[];
  prioridade?: Prioridade;
  menu_pendente?: boolean;
}

export const PRIORIDADES = ['baixa', 'normal', 'alta', 'urgente'] as const;
export type Prioridade = (typeof PRIORIDADES)[number];
export const DIAS_SEMANA = ['1', '2', '3', '4', '5', '6', '0'] as const;

export interface Faixa {
  inicio: string;
  fim: string;
}
export interface Horario {
  dias: Partial<Record<(typeof DIAS_SEMANA)[number], Faixa[]>>;
  feriados: string[];
}

/** Anexo de mensagem (mídia baixada, anexo de e-mail ou arquivo enviado). */
export interface AnexoMensagem {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
  familia: 'imagem' | 'audio' | 'video' | 'documento' | string;
  /** U11 (lote 1): anexo de e-mail acima de 1 MB que ficou no provedor (baixado ao abrir). */
  sob_demanda?: boolean;
  /** U11 (lote 1): arquivo removido pela retenção da caixa (o nome fica). */
  expurgado?: boolean;
}

/** Dados do e-mail guardados na mensagem (HTML já sanitizado no servidor). */
export interface DadosEmail {
  de?: string;
  de_nome?: string | null;
  para?: string[];
  cc?: string[];
  assunto?: string;
  html?: string;
  imagens_remotas?: number;
  message_id?: string | null;
}

/** U11 (lote 1): caixa de e-mail monitorada, com a retenção e o último cálculo do uso de disco. */
export interface CaixaEmail {
  endereco: string;
  sincronizado_em: string | null;
  ultimo_erro: string | null;
  erros_seguidos: number;
  retencao_meses?: number | null;
  uso?: { texto: number; anexos: number; provedor: number; mensagens: number; calculado_em: string } | null;
  /** U11 (lote 2): quem pede lê a caixa (false: só aparece a quem configura, para saber de quem é). */
  le?: boolean;
  tipo?: TipoCaixa;
  dono?: { id: string; nome: string | null } | null;
  ignorar_categorias?: boolean;
  delegados?: { id: string; usuario_id: string; nome: string | null }[] | null;
  pode?: { configurar: boolean; delegar: boolean; decidir?: boolean };
  /** Trajetórias (item 2): caixa pessoal de quem foi desligado, bloqueada até o gestor decidir (ou encerrada). */
  bloqueio?: { desde: string; encerrada: boolean } | null;
  delegada?: boolean;
  minha_delegacao_id?: string | null;
}

/** U11 (lote 2): atendimento (filas), corporativa (de setor, por delegação) ou pessoal (só o dono e os delegados). */
export type TipoCaixa = 'atendimento' | 'corporativa' | 'pessoal';
export const TIPOS_CAIXA: TipoCaixa[] = ['atendimento', 'corporativa', 'pessoal'];

export interface EstadoCaixas {
  conector: string;
  retencoes?: number[];
  caixas: CaixaEmail[];
  /** Review r56 (SEG-B1): o uso soma todas as empresas; só quem vê todas o recebe. */
  uso_visivel?: boolean;
}

export const CHAVE_CAIXAS_EMAIL = ['conversas', 'email', 'caixas'] as const;

/**
 * U11 (lote 1): link para abrir no Gmail o e-mail cujo corpo saiu pela retenção, pela busca do Message-ID na conta da
 * caixa (só abre para quem entra no Gmail com essa conta). Sem Message-ID válido, null.
 */
export function linkNoGmail(caixa: string | null | undefined, messageId: string | null | undefined): string | null {
  if (!caixa || !messageId || !/^<[^<>\s]{3,250}>$/.test(messageId)) return null;
  return `https://mail.google.com/mail/u/${encodeURIComponent(caixa)}/#search/${encodeURIComponent(`rfc822msgid:${messageId.slice(1, -1)}`)}`;
}

/**
 * Review r56 (ROB-B3 = SPEC-B3): a caixa do `?caixa=` saneada, como os outros valores da URL (r27, r29): só endereço de
 * e-mail até 320 caracteres e, com as caixas já carregadas, só uma das monitoradas. Fora disso, '' ("Todas as caixas"),
 * sem erro na lista nem opção inventada na seleção.
 */
export function caixaDaUrl(bruto: string | null | undefined, monitoradas: readonly string[] | null): string {
  const v = (bruto ?? '').trim().toLowerCase();
  if (!v || v.length > 320 || !/^[^\s@]+@[^\s@]+$/.test(v)) return '';
  return !monitoradas || monitoradas.includes(v) ? v : '';
}

/** U11 (lote 1): tamanho do uso de disco (zero aparece como 0 KB, não 1 KB). */
export function tamanhoUso(bytes: number, idioma: string): string {
  return bytes > 0 ? tamanhoArquivo(bytes, idioma) : tamanhoArquivo(1024, idioma).replace(/\d+/, '0');
}

export interface Fila {
  id: string;
  nome: string;
  distribuicao: 'manual' | 'rodizio' | 'menor_carga';
  canais: Canal[];
  ativa: boolean;
  membros: { usuario_id: string; nome: string; disponivel?: boolean; carga?: number }[];
  conversas_ativas: number;
  horario?: Horario | null;
  mensagem_fora_horario?: string | null;
  limite_por_atendente?: number | null;
  /** Trajetórias (item 4): lead novo ganha oportunidade no funil (nulo = o padrão). */
  oportunidade_automatica?: boolean;
  oportunidade_funil_id?: string | null;
}

/** Trilha 5b: menu de entrada (URA numérica) do canal. */
export interface Menu {
  id: string;
  canal: 'whatsapp' | 'instagram';
  ativo: boolean;
  mensagem: string;
  opcoes: { tecla: string; rotulo: string; fila_id: string }[];
  mensagem_invalida: string | null;
  updated_at?: string;
}

/** Trilha 5b: resposta automática por palavra-chave. */
export interface PalavraChave {
  id: string;
  canal: 'whatsapp' | 'instagram' | null;
  fila_id: string | null;
  fila_nome: string | null;
  palavras: string[];
  resposta: string;
  ativa: boolean;
}

/** A janela de 24 h do WhatsApp está aberta? (Fora do WhatsApp, sempre.) */
export function janelaAberta(c: Pick<Conversa, 'canal' | 'janela_expira_em'>, agora = Date.now()): boolean {
  if (c.canal !== 'whatsapp') return true;
  return !!c.janela_expira_em && new Date(c.janela_expira_em).getTime() > agora;
}

/**
 * Review r23 visual (B1): o contato do cabeçalho da conversa. No canal do Portal, o contato gravado é o identificador interno
 * `portal:<acesso>` e nunca vai à tela. Nos outros canais, o telefone (WhatsApp) ou o contato, pelos formatadores do tipo.
 * Review r26 spec (O2): no Portal vale uma regra só, o nome do cliente (pela regra de nomes do modo discreto, `nomePessoa`)
 * ou, sem ele, o rótulo do canal. Antes, o cabeçalho abria com o cliente e trocava para quem escreveu pelo portal quando as
 * mensagens carregavam (o nome saltava na mesma conversa); por isso a função não olha mais as mensagens.
 */
/** Nome do contato na lista e no cabeçalho: o parceiro (`nomeParceiro`) ou, sem parceiro (caixa pessoal, r64 SEG-M2), o e-mail pelo `contato`. */
export function nomeDoContato(c: Pick<Conversa, 'parceiro_id' | 'parceiro_nome' | 'contato_externo'>): string {
  return c.parceiro_id ? nomeParceiro(c.parceiro_nome) : contato(c.contato_externo);
}

export function contatoDoCabecalho(c: Pick<Conversa, 'canal' | 'contato_externo' | 'parceiro_nome' | 'parceiro_telefone'>, rotuloCanal: string): string {
  // Item 11 do lote de 19/10: no chat do site, a mesma regra do Portal (o contato `site:<visitante>` nunca vai à tela; a
  // conversa do site sempre tem o contato do Cadastros com nome, então o rótulo de reserva não aparece).
  if (c.canal === 'portal' || c.canal === 'site') return c.parceiro_nome ? nomePessoa(c.parceiro_nome) : rotuloCanal;
  if (c.canal === 'whatsapp') return telefoneFormatado(c.parceiro_telefone) || contato(c.contato_externo);
  return contato(c.contato_externo);
}

/**
 * Review r27 spec (O2): quem escreveu pelo portal (o nome gravado na mensagem de entrada, `dados.portal.nome`), pela regra de
 * nomes do modo discreto (`nomePessoa`). O cabeçalho segue com o cliente (r26 spec O2); o nome vai em cada mensagem, no mesmo
 * lugar do nome de quem enviou pela equipe, então duas pessoas do mesmo cliente se distinguem na conversa.
 */
export function autorDoPortal(m: Pick<Mensagem, 'direcao' | 'dados'>): string | null {
  if (m.direcao !== 'entrada') return null;
  const nome = (m.dados?.portal as { nome?: unknown } | undefined)?.nome;
  return typeof nome === 'string' && nome.trim() ? nomePessoa(nome.trim()) : null;
}

/** Anexos de uma mensagem: os do envio (`anexos`) ou a mídia baixada (`anexo_id`). */
export function anexosDaMensagem(dados: Record<string, unknown>): AnexoMensagem[] {
  if (Array.isArray(dados.anexos)) return (dados.anexos as AnexoMensagem[]).filter((a) => a && typeof a.id === 'string');
  if (typeof dados.anexo_id === 'string')
    return [{ id: dados.anexo_id, nome: String(dados.nome_arquivo ?? ''), mime: String(dados.mime ?? ''), tamanho: Number(dados.tamanho ?? 0), familia: String(dados.familia ?? 'documento') }];
  return [];
}

export const urlAnexo = (conversaId: string, anexoId: string) => `/api/v1/conversas/conversas/${conversaId}/anexos/${anexoId}`;

/** Tamanho legível (KB, MB) no idioma do app. */
export function tamanhoArquivo(bytes: number, idioma: string): string {
  const nf = (v: number, casas: number) => new Intl.NumberFormat(idioma === 'pt' ? 'pt-BR' : idioma === 'es' ? 'es' : 'en', { maximumFractionDigits: casas }).format(v);
  if (bytes >= 1024 * 1024) return `${nf(bytes / (1024 * 1024), 1)} MB`;
  return `${nf(Math.max(1, Math.round(bytes / 1024)), 0)} KB`;
}

export interface Mensagem {
  id: string;
  conversa_id: string;
  direcao: 'entrada' | 'saida';
  tipo: 'texto' | 'template' | 'midia' | 'sistema';
  conteudo: string;
  dados: Record<string, unknown>;
  status: StatusMensagem;
  erro: string | null;
  enviada_por: string | null;
  enviada_por_nome: string | null;
  created_at: string;
}

export interface EstadoCanais {
  whatsapp: boolean;
  /** Fase 2 (W30): provedor do WhatsApp em uso (só a API oficial tem a janela de 24 h e os templates). */
  whatsapp_provedor?: 'oficial' | 'baileys' | 'whatsmeow' | 'zernio' | null;
  instagram: boolean;
  email?: boolean;
  /** Portal do Cliente (Onda 1): canal do app do cliente (módulo Portal instalado). */
  portal?: boolean;
  /** Item 11 do lote de 19/10: chat do site (sempre disponível; os widgets ficam em Conversas > Chat do site). */
  site?: boolean;
  pipeline: boolean;
  agenda: boolean;
}

export interface Resposta {
  id: string;
  atalho: string;
  texto: string;
}

export interface TemplateItem {
  id: string;
  nome: string;
  idioma: string;
  categoria: 'marketing' | 'utilidade' | 'autenticacao';
  status: 'aprovado' | 'pendente' | 'rejeitado';
  corpo: string;
  componentes: unknown;
  sincronizado_em: string | null;
  /** U14 F4: criado no MalgoOS ou sincronizado; situação no provedor, motivo, qualidade e mudança de categoria. */
  origem: 'sincronizado' | 'criado';
  provedor: 'meta' | 'zernio' | null;
  id_externo: string | null;
  situacao_provedor: string | null;
  motivo_rejeicao: string | null;
  enviado_aprovacao_em: string | null;
  envio_estado: 'enviando' | 'erro' | null;
  envio_erro: string | null;
  qualidade: string | null;
  categoria_anterior: TemplateItem['categoria'] | null;
  categoria_alterada_em: string | null;
  categoria_prevista: TemplateItem['categoria'] | null;
  categoria_prevista_em: string | null;
  amostra_anexo_id: string | null;
}

export interface PaginaApi<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/** Quantas variáveis ({{1}}, {{2}}...) o corpo do template pede. */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  return ((partes[0]?.[0] ?? '?') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

/** Telefone E.164 brasileiro para exibição: +5511987651234 vira (11) 98765-1234. */
/** Telefone pelo formatador central (que respeita o modo discreto). */
export { telefoneFormatado } from '../../nucleo/formato';

/** Review r46 robustez (M4): lista de etiquetas da caixa, fora de ['conversas'] (o sinal do tempo real não a refaz). */
export const CHAVE_ETIQUETAS = ['conversas-etiquetas'] as const;
